'use client';

import { c as _c } from "react/compiler-runtime";
import type { VariantProps } from 'class-variance-authority';
import dynamic from 'next/dynamic';
import type { ComponentPropsWithoutRef } from 'react';
import type { ButtonProps } from '@/app/components/button';
import { Button } from '@/app/components/button';
import { IconLink } from '@/app/components/icons/communication/icon-link';
import { IconFacebook } from '@/app/components/icons/social-media/icon-facebook';
import { IconLine } from '@/app/components/icons/social-media/icon-line';
import { SOCIAL_MEDIA_DATA_GA, SOCIAL_MEDIA_DATA_TEST } from '@/app/components/social-media/tracking';
import { useSocialMediaShare } from '@/app/hooks/useSocialMediaShare';
import type { SocialMediaType } from '@/app/types/social-media';
import { cnva } from '@/app/utils/cnva';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { createDataGA } from '@/app/utils/createDataGA';
const Toast = dynamic(() => import('@/app/components/overlays/toast').then(mod => mod.Toast), {
  ssr: false
});
const SocialMediaShareGroupStyled = cnva('flex flex-wrap items-center justify-center gap-2 xl:justify-start', {
  variants: {
    theme: {
      light: '',
      dark: ''
    }
  }
});
const FACEBOOK: SocialMediaType = 'facebook';
const LINE: SocialMediaType = 'line';
const SHARE_ARIA_LABEL = 'แชร์ไปยัง';
const COPY = 'copy';
const COPY_ARIA_LABEL = 'คัดลอกลิงก์';
interface SocialMediaShareGroupProps extends Omit<ComponentPropsWithoutRef<'aside'>, 'className' | 'children'> {
  theme?: VariantProps<typeof SocialMediaShareGroupStyled>['theme'];
  className?: string;
  restToastProps?: Record<string, string>;
}
export const SocialMediaShareGroup = t0 => {
  const $ = _c(49);
  let className;
  let rest;
  let restToastProps;
  let t1;
  if ($[0] !== t0) {
    ({
      theme: t1,
      className,
      restToastProps,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = rest;
    $[3] = restToastProps;
    $[4] = t1;
  } else {
    className = $[1];
    rest = $[2];
    restToastProps = $[3];
    t1 = $[4];
  }
  const theme = t1 === undefined ? "light" : t1;
  const {
    toast,
    onShare,
    onCopyLink,
    closeToast
  } = useSocialMediaShare();
  const t2 = theme === "light" ? "black" : "white";
  let t3;
  if ($[5] !== t2) {
    t3 = {
      size: "sm",
      variant: "secondary",
      color: t2,
      tabIndex: 0,
      className: "size-10"
    };
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const buttonBaseProps = t3;
  let t4;
  if ($[7] !== className || $[8] !== theme) {
    t4 = SocialMediaShareGroupStyled({
      className,
      theme
    });
    $[7] = className;
    $[8] = theme;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = createDataAttributes({
      test: SOCIAL_MEDIA_DATA_TEST.share
    });
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <IconFacebook />;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  let t8;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = createDataAttributes({
      test: `${SOCIAL_MEDIA_DATA_TEST.share}-${FACEBOOK}`
    });
    t8 = createDataGA({
      click: true,
      eventName: SOCIAL_MEDIA_DATA_GA.event,
      group: SOCIAL_MEDIA_DATA_GA.group.share,
      value: FACEBOOK
    });
    $[12] = t7;
    $[13] = t8;
  } else {
    t7 = $[12];
    t8 = $[13];
  }
  let t9;
  if ($[14] !== onShare) {
    t9 = () => onShare(FACEBOOK);
    $[14] = onShare;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== buttonBaseProps || $[17] !== t9) {
    t10 = <Button aria-label={`${SHARE_ARIA_LABEL} ${FACEBOOK}`} iconBefore={t6} {...buttonBaseProps} {...t7} {...t8} onClick={t9} />;
    $[16] = buttonBaseProps;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <IconLine />;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  let t13;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = createDataAttributes({
      test: `${SOCIAL_MEDIA_DATA_TEST.share}-${LINE}`
    });
    t13 = createDataGA({
      click: true,
      eventName: SOCIAL_MEDIA_DATA_GA.event,
      group: SOCIAL_MEDIA_DATA_GA.group.share,
      value: LINE
    });
    $[20] = t12;
    $[21] = t13;
  } else {
    t12 = $[20];
    t13 = $[21];
  }
  let t14;
  if ($[22] !== onShare) {
    t14 = () => onShare(LINE);
    $[22] = onShare;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== buttonBaseProps || $[25] !== t14) {
    t15 = <Button aria-label={`${SHARE_ARIA_LABEL} ${LINE}`} iconBefore={t11} {...buttonBaseProps} {...t12} {...t13} onClick={t14} />;
    $[24] = buttonBaseProps;
    $[25] = t14;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <IconLink />;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  let t18;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = createDataAttributes({
      test: `${SOCIAL_MEDIA_DATA_TEST.share}-${COPY}`
    });
    t18 = createDataGA({
      click: true,
      eventName: SOCIAL_MEDIA_DATA_GA.event,
      group: SOCIAL_MEDIA_DATA_GA.group.share,
      value: COPY
    });
    $[28] = t17;
    $[29] = t18;
  } else {
    t17 = $[28];
    t18 = $[29];
  }
  let t19;
  if ($[30] !== buttonBaseProps || $[31] !== onCopyLink) {
    t19 = <Button iconBefore={t16} aria-label={COPY_ARIA_LABEL} {...buttonBaseProps} {...t17} {...t18} onClick={onCopyLink} />;
    $[30] = buttonBaseProps;
    $[31] = onCopyLink;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  let t20;
  if ($[33] !== rest || $[34] !== t10 || $[35] !== t15 || $[36] !== t19 || $[37] !== t4) {
    t20 = <aside {...rest} className={t4} {...t5}>{t10}{t15}{t19}</aside>;
    $[33] = rest;
    $[34] = t10;
    $[35] = t15;
    $[36] = t19;
    $[37] = t4;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] !== closeToast || $[40] !== restToastProps || $[41] !== toast?.isOpen || $[42] !== toast?.message || $[43] !== toast?.title || $[44] !== toast?.type) {
    t21 = !!toast?.isOpen && <Toast theme="dark" {...restToastProps} type={toast?.type} title={toast?.title} description={toast?.message} onClose={closeToast} />;
    $[39] = closeToast;
    $[40] = restToastProps;
    $[41] = toast?.isOpen;
    $[42] = toast?.message;
    $[43] = toast?.title;
    $[44] = toast?.type;
    $[45] = t21;
  } else {
    t21 = $[45];
  }
  let t22;
  if ($[46] !== t20 || $[47] !== t21) {
    t22 = <>{t20}{t21}</>;
    $[46] = t20;
    $[47] = t21;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  return t22;
};